import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PackagePlus, Eye, Trash2 } from 'lucide-react';
import {
  listProduzioni, registrazioneEcomaticoProduzioni, deleteProduzione,
  type ProduzioneListItem, type ListaFiltri,
} from '../api/magazzino';
import { listAreas, type Area } from '../api/areas';
import { getCerRifiutiProdotti, type CerRifiutoProdotto } from '../api/settings';
import { listOperatori, type OperatoreItem } from '../api/users';
import ProduzioneDettaglioModal from '../components/produzioni/ProduzioneDettaglioModal';
import EliminaMagazzinoModal from '../components/magazzino/EliminaMagazzinoModal';
import { EcomaticoBadge, EcomaticoFiltro, EcomaticoBulkBar, RiepilogoSelezione } from '../components/magazzino/Ecomatico';
import { useAuthStore } from '../store/authStore';
import { fmtKg } from '../lib/saldo';
import { canEliminaMagazzino } from '../lib/constants';

const inputCls = 'text-sm text-ink-dark border border-stone-border rounded px-2 py-1.5 focus:outline-none focus:border-sage bg-white';

export default function ProduzioniPage() {
  const { t } = useTranslation();
  const [items, setItems] = useState<ProduzioneListItem[]>([]);
  const [areas, setAreas] = useState<Area[]>([]);
  const [cerOptions, setCerOptions] = useState<CerRifiutoProdotto[]>([]);
  const [operatori, setOperatori] = useState<OperatoreItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [areaId, setAreaId] = useState('');
  const [materiale, setMateriale] = useState('');
  const [dataDa, setDataDa] = useState('');
  const [dataA, setDataA] = useState('');
  const [operatoreId, setOperatoreId] = useState('');
  const [filtroEco, setFiltroEco] = useState<boolean | undefined>(undefined);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [eliminando, setEliminando] = useState<ProduzioneListItem | null>(null);
  const [esito, setEsito] = useState<string | null>(null);
  const userRole = useAuthStore((s) => s.user?.role ?? '');
  const canDelete = canEliminaMagazzino(userRole);

  const reload = useCallback(() => {
    void (async () => {
      setLoading(true);
      try {
        const filtri: ListaFiltri = {};
        if (areaId) filtri.area_id = areaId;
        if (materiale) filtri.materiale = materiale;
        if (dataDa) filtri.data_da = dataDa;
        if (dataA) filtri.data_a = dataA;
        if (operatoreId) filtri.operatore_id = operatoreId;
        if (filtroEco !== undefined) filtri.registrato_ecomatico = filtroEco;
        setItems(await listProduzioni(filtri));
        setSelected(new Set());
      } finally {
        setLoading(false);
      }
    })();
  }, [areaId, materiale, dataDa, dataA, operatoreId, filtroEco]);
  useEffect(() => { reload(); }, [reload]);

  useEffect(() => {
    void (async () => {
      setAreas((await listAreas()).filter((a) => a.categoria === 'RP'));
      setCerOptions(await getCerRifiutiProdotti(true));
      setOperatori(await listOperatori());
    })();
  }, []);

  function toggle(id: string) {
    setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  }
  const allSelected = items.length > 0 && selected.size === items.length;
  function toggleAll() {
    setSelected(allSelected ? new Set() : new Set(items.map((i) => i.id)));
  }

  async function applica(ids: string[], registrato: boolean) {
    if (ids.length === 0) return;
    setBusy(true);
    try { await registrazioneEcomaticoProduzioni(ids, registrato); reload(); }
    finally { setBusy(false); }
  }

  // Riepilogo della selezione: client-side sulle righe gia' in pagina. La tipologia
  // sta sui BIG BAG, quindi una produzione contribuisce a piu' voci.
  const selezionate = items.filter((i) => selected.has(i.id));
  const perCer = selezionate.map((i) => ({ chiave: i.materiale, kg: i.peso_kg }));
  const perTipologia = selezionate.flatMap((i) => (i.tipologie ?? []).map((tp) => ({
    chiave: tp.nome ?? t('magazzino.ecomatico.tipologia_non_specificata'), kg: tp.kg,
  })));

  async function confermaElimina(it: ProduzioneListItem) {
    const res = await deleteProduzione(it.id);
    setEliminando(null);
    setEsito(t('magazzino.elimina.esito_produzione', {
      kg: fmtKg(it.peso_kg),
      area: it.area_codice ?? '—',
      saldo: fmtKg(res.saldo_risultante_kg),
    }));
    reload();
  }

  return (
    <div className="p-6">
      <div className="flex items-center gap-2 mb-5">
        <PackagePlus size={20} className="text-sage" />
        <h1 className="text-xl font-semibold text-ink-dark">{t('magazzino.produzioni_page.titolo')}</h1>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <select className={inputCls} value={areaId} onChange={(e) => setAreaId(e.target.value)}>
          <option value="">{t('magazzino.produzioni_page.tutte_aree')}</option>
          {areas.map((a) => <option key={a.id} value={a.id}>{a.codice}</option>)}
        </select>
        <select className={inputCls} value={materiale} onChange={(e) => setMateriale(e.target.value)}>
          <option value="">{t('magazzino.scarichi_page.tutti_cer')}</option>
          {cerOptions.map((c) => (
            <option key={c.cer} value={c.cer}>{c.nome_descrittivo ? `${c.cer} — ${c.nome_descrittivo}` : c.cer}</option>
          ))}
        </select>
        <input type="date" className={inputCls} value={dataDa} onChange={(e) => setDataDa(e.target.value)} />
        <input type="date" className={inputCls} value={dataA} onChange={(e) => setDataA(e.target.value)} />
        <select className={inputCls} value={operatoreId} onChange={(e) => setOperatoreId(e.target.value)}>
          <option value="">{t('magazzino.scarichi_page.tutti_operatori')}</option>
          {operatori.map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}
        </select>
        <EcomaticoFiltro value={filtroEco} onChange={setFiltroEco} />
      </div>

      {selected.size > 0 && (
        <EcomaticoBulkBar
          count={selected.size} busy={busy}
          onRegistra={() => applica([...selected], true)}
          onDaRegistrare={() => applica([...selected], false)}
        >
          <RiepilogoSelezione
            titolo={t('magazzino.ecomatico.riepilogo_cer')}
            voci={perCer} mostraN testid="riepilogo-cer"
          />
          <RiepilogoSelezione
            titolo={t('magazzino.ecomatico.riepilogo_tipologia')}
            voci={perTipologia} testid="riepilogo-tipologia"
          />
        </EcomaticoBulkBar>
      )}

      <div className="bg-white border border-stone-mid rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-stone-bg">
            <tr className="text-left text-xs font-semibold text-ink-soft uppercase tracking-wide">
              <th className="px-3 py-3 w-8">
                <input type="checkbox" checked={allSelected} onChange={toggleAll} aria-label={t('magazzino.scarichi_page.seleziona_tutto')} />
              </th>
              <th className="px-3 py-3">{t('magazzino.scarichi_page.col_data')}</th>
              <th className="px-3 py-3">{t('magazzino.scarichi_page.col_area')}</th>
              <th className="px-3 py-3">{t('magazzino.scarichi_page.col_materiale')}</th>
              <th className="px-3 py-3 text-right">{t('magazzino.scarichi_page.col_peso')}</th>
              <th className="px-3 py-3 text-right">{t('magazzino.produzioni_page.col_bigbag')}</th>
              <th className="px-3 py-3">{t('magazzino.scarichi_page.col_operatore')}</th>
              <th className="px-3 py-3">{t('magazzino.scarichi_page.col_ecomatico')}</th>
              <th className="px-3 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="px-3 py-8 text-center text-ink-soft">{t('magazzino.scarichi_page.caricamento')}</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={9} className="px-3 py-8 text-center text-ink-soft">{t('magazzino.produzioni_page.vuoto')}</td></tr>
            ) : items.map((it) => (
              <tr key={it.id} className="border-t border-stone-mid/60 hover:bg-stone-bg/50 cursor-pointer" onClick={() => setOpenId(it.id)}>
                <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                  <input type="checkbox" checked={selected.has(it.id)} onChange={() => toggle(it.id)} aria-label={t('magazzino.scarichi_page.seleziona_riga')} />
                </td>
                <td className="px-3 py-2.5">{new Date(it.created_at).toLocaleDateString('it-IT')}</td>
                <td className="px-3 py-2.5 font-medium">{it.area_codice ?? '—'}</td>
                <td className="px-3 py-2.5">{it.materiale}</td>
                <td className="px-3 py-2.5 text-right">{fmtKg(it.peso_kg)}</td>
                <td className="px-3 py-2.5 text-right">{it.num_big_bags}</td>
                <td className="px-3 py-2.5 text-ink-mid">{it.operatore_nome ?? '—'}</td>
                <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center gap-2">
                    <EcomaticoBadge registrato={it.registrato_ecomatico} at={it.registrato_ecomatico_at} daNome={it.registrato_ecomatico_da_nome} />
                    <button type="button" disabled={busy}
                      onClick={() => applica([it.id], !it.registrato_ecomatico)}
                      className="text-xs text-ink-soft hover:text-sage-dark disabled:opacity-50">
                      {it.registrato_ecomatico ? t('magazzino.scarichi_page.segna_da_registrare') : t('magazzino.scarichi_page.segna_registrato')}
                    </button>
                  </div>
                </td>
                <td className="px-3 py-2.5 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                  <span className="text-ink-soft hover:text-sage-dark inline-flex items-center gap-1 text-xs cursor-pointer mr-3" onClick={() => setOpenId(it.id)}><Eye size={13} /> {t('magazzino.produzioni_page.dettaglio')}</span>
                  {canDelete && (
                    <button type="button" onClick={() => { setEsito(null); setEliminando(it); }}
                      disabled={it.registrato_ecomatico}
                      title={it.registrato_ecomatico ? t('magazzino.elimina.disabilitato_ecomatico') : undefined}
                      data-testid={`btn-elimina-${it.id}`}
                      className="text-red-600 hover:text-red-700 inline-flex items-center gap-1 text-xs disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-red-600">
                      <Trash2 size={13} /> {t('magazzino.elimina.azione')}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {esito && (
        <div className="mt-3 px-3 py-2 bg-sage-light border border-sage/30 rounded text-sm text-sage-dark" data-testid="produzione-esito">
          {esito}
        </div>
      )}

      {openId && (
        <ProduzioneDettaglioModal
          produzioneId={openId}
          canEdit
          onClose={() => setOpenId(null)}
          onChanged={reload}
        />
      )}

      {eliminando && (
        <EliminaMagazzinoModal
          title={t('magazzino.elimina.titolo_produzione')}
          righe={[
            t('magazzino.elimina.riga_produzione', {
              kg: fmtKg(eliminando.peso_kg),
              n: eliminando.num_big_bags,
              area: eliminando.area_codice ?? '—',
            }),
            t('magazzino.elimina.riga_qr'),
          ]}
          avvertenza={t('magazzino.elimina.avvertenza_ecomatico')}
          onConfirm={() => confermaElimina(eliminando)}
          onClose={() => setEliminando(null)}
          testid="elimina-produzione-modal"
        />
      )}
    </div>
  );
}
